import React, { useState } from 'react';
import {
  Table, Tag, Space, Button, Card, Form, Input, Select, DatePicker,
  Typography, message,
} from 'antd';
import { SearchOutlined, ReloadOutlined, ExportOutlined, SendOutlined } from '@ant-design/icons';
import IssueDrawer from './components/IssueDrawer';
import {
  COUPON_TYPES, CODE_STATUS, TARGET_TYPES, MOCK_COUPONS, MOCK_CODES,
  getDomainNode, getIssueSource, getIssueStatus,
} from './couponData';

const { RangePicker } = DatePicker;
const { Title, Text } = Typography;

const getCoupon = (id) => MOCK_COUPONS.find((c) => c.id === id);

// 发放记录 = 已发放的券码（未发放券码不属于发放记录）
const ISSUE_RECORDS = MOCK_CODES.filter((c) => c.issuedAt && c.issuedAt !== '-');

const CouponIssueRecords = () => {
  const [searchForm] = Form.useForm();
  const [filters, setFilters] = useState({});
  const [issueVisible, setIssueVisible] = useState(false);

  const handleSearch = () => {
    setFilters(searchForm.getFieldsValue());
  };

  const handleResetSearch = () => {
    searchForm.resetFields();
    setFilters({});
  };

  const filteredRecords = ISSUE_RECORDS.filter((r) => {
    const { code, couponName, couponId, target, targetType, status, issuedRange } = filters;
    const coupon = getCoupon(r.couponId);
    if (code) {
      const kw = String(code).trim().toLowerCase();
      if (!r.code.toLowerCase().includes(kw)) return false;
    }
    if (couponName) {
      const kw = String(couponName).trim().toLowerCase();
      if (!(coupon?.name || '').toLowerCase().includes(kw)) return false;
    }
    if (couponId) {
      const kw = String(couponId).trim().toLowerCase();
      if (!r.couponId.toLowerCase().includes(kw)) return false;
    }
    if (target) {
      const kw = String(target).trim().toLowerCase();
      if (!String(r.target).toLowerCase().includes(kw)) return false;
    }
    if (targetType && r.targetType !== targetType) return false;
    if (status && r.status !== status) return false;
    if (issuedRange && issuedRange.length === 2 && issuedRange[0] && issuedRange[1]) {
      const start = issuedRange[0].format('YYYY-MM-DD');
      const end = issuedRange[1].format('YYYY-MM-DD');
      const issuedDate = String(r.issuedAt).slice(0, 10);
      if (issuedDate < start || issuedDate > end) return false;
    }
    return true;
  });

  const handleExport = () => {
    if (filteredRecords.length === 0) {
      message.warning('没有可导出的数据');
      return;
    }
    // TODO: 联调时替换为接口导出
    const headers = ['券码', '所属卡券ID', '所属卡券名称', '券类型', '适用节点', '发放目标', '发放目标类型', '发放目标UID', '发放源', '发放时间', '发放状态', '核销目标', '核销目标类型', '核销目标UID', '核销状态'];
    const rows = filteredRecords.map((r) => [
      r.code,
      r.couponId,
      getCoupon(r.couponId)?.name || '-',
      COUPON_TYPES[getCoupon(r.couponId)?.type]?.label || '-',
      getDomainNode(r.domainNode).label,
      r.target,
      r.targetType,
      r.targetUid || '-',
      getIssueSource(r.issueSource).label,
      r.issuedAt,
      getIssueStatus(r.issueStatus).label,
      r.usedTarget || '-',
      r.usedViaType || '-',
      r.usedUid || '-',
      CODE_STATUS[r.status].label,
    ].join(','));
    const csvContent = [headers.join(','), ...rows].join('\n');
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `发放记录导出_${new Date().toLocaleDateString()}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    message.success('导出成功');
  };

  const columns = [
    { title: '券码', dataIndex: 'code', key: 'code', width: 160 },
    {
      title: '所属卡券',
      key: 'coupon',
      width: 220,
      render: (_, record) => (
        <div style={{ fontSize: '12px' }}>
          <div>{getCoupon(record.couponId)?.name || '-'}</div>
          <div style={{ color: '#999' }}>ID: {record.couponId}</div>
        </div>
      ),
    },
    {
      title: '券类型',
      key: 'type',
      width: 100,
      render: (_, record) => {
        const type = getCoupon(record.couponId)?.type;
        if (!type) return '-';
        return <Tag color={COUPON_TYPES[type].color}>{COUPON_TYPES[type].label}</Tag>;
      },
    },
    {
      title: '适用节点',
      key: 'domainNode',
      width: 110,
      render: (_, record) => {
        const node = getDomainNode(record.domainNode);
        return <Tag color={node.color}>{node.label}</Tag>;
      },
    },
    {
      title: '发放目标',
      key: 'target',
      width: 200,
      render: (_, record) => (
        <div style={{ fontSize: '12px' }}>
          <div>{record.target}</div>
          <div style={{ color: '#999' }}>{record.targetType}</div>
        </div>
      ),
    },
    {
      title: '发放目标(UID)',
      key: 'targetUid',
      width: 180,
      render: (_, record) => record.targetUid || <span style={{ color: '#ccc' }}>-</span>,
    },
    {
      title: '发放源',
      key: 'issueSource',
      width: 110,
      render: (_, record) => {
        const s = getIssueSource(record.issueSource);
        return <Tag color={s.color}>{s.label}</Tag>;
      },
    },
    { title: '发放时间', dataIndex: 'issuedAt', key: 'issuedAt', width: 170 },
    {
      title: '发放状态',
      key: 'issueStatus',
      width: 110,
      render: (_, record) => {
        const s = getIssueStatus(record.issueStatus);
        return <Tag color={s.color}>{s.label}</Tag>;
      },
    },
    {
      title: '核销目标',
      key: 'usedTarget',
      width: 200,
      render: (_, record) => (
        <div style={{ fontSize: '12px' }}>
          <div>{record.usedTarget || '-'}</div>
          <div style={{ color: '#999' }}>{record.usedViaType || '-'}</div>
        </div>
      ),
    },
    {
      title: '核销目标(UID)',
      key: 'usedUid',
      width: 180,
      render: (_, record) => record.usedUid || <span style={{ color: '#ccc' }}>-</span>,
    },
    {
      title: '核销状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status) => <Tag color={CODE_STATUS[status].color}>{CODE_STATUS[status].label}</Tag>,
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ marginBottom: 16 }}>
        <Title level={4} style={{ marginBottom: 8 }}>发放记录</Title>
        <Text type="secondary" style={{ fontSize: 12 }}>
          展示已发放的券码记录（按券码逐条）；发放为可选的定向预分配，未发放券码同样可被用户凭码核销。仅平台运营可见，记录只读。
        </Text>
      </div>

      <Card style={{ marginBottom: '16px' }}>
        <Form form={searchForm} layout="inline" onFinish={handleSearch}>
          <Form.Item name="code" style={{ marginBottom: 0 }}>
            <Input placeholder="券码" style={{ width: 160 }} allowClear />
          </Form.Item>
          <Form.Item name="couponName" style={{ marginBottom: 0 }}>
            <Input placeholder="卡券名称" style={{ width: 160 }} allowClear />
          </Form.Item>
          <Form.Item name="couponId" style={{ marginBottom: 0 }}>
            <Input placeholder="卡券ID" style={{ width: 160 }} allowClear />
          </Form.Item>
          <Form.Item name="target" style={{ marginBottom: 0 }}>
            <Input placeholder="发放目标" style={{ width: 180 }} allowClear />
          </Form.Item>
          <Form.Item name="targetType" style={{ marginBottom: 0 }}>
            <Select placeholder="发放目标类型" style={{ width: 140 }} allowClear>
              {TARGET_TYPES.map((t) => (
                <Select.Option key={t} value={t}>{t}</Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="status" style={{ marginBottom: 0 }}>
            <Select placeholder="状态" style={{ width: 130 }} allowClear>
              {Object.entries(CODE_STATUS).map(([value, cfg]) => (
                <Select.Option key={value} value={value}>{cfg.label}</Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="issuedRange" style={{ marginBottom: 0 }}>
            <RangePicker style={{ width: 240 }} placeholder={['发放开始时间', '发放结束时间']} />
          </Form.Item>
          <Form.Item style={{ marginBottom: 0 }}>
            <Space>
              <Button type="primary" htmlType="submit" icon={<SearchOutlined />}>查询</Button>
              <Button icon={<ReloadOutlined />} onClick={handleResetSearch}>重置</Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>

      <Card>
        <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ color: '#999', fontSize: 13 }}>共 {filteredRecords.length} 条发放记录</span>
          <Space>
            <Button type="primary" icon={<SendOutlined />} onClick={() => setIssueVisible(true)}>发起发放</Button>
            <Button icon={<ExportOutlined />} onClick={handleExport}>导出</Button>
          </Space>
        </div>
        <Table
          columns={columns}
          dataSource={filteredRecords}
          scroll={{ x: 1880 }}
          pagination={{ pageSize: 10, showSizeChanger: true, showTotal: (t) => `共 ${t} 条记录` }}
        />
      </Card>

      {/* 发起发放（需在 Drawer 内选择卡券） */}
      <IssueDrawer
        open={issueVisible}
        coupons={MOCK_COUPONS}
        codes={MOCK_CODES}
        onClose={() => setIssueVisible(false)}
      />
    </div>
  );
};

export default CouponIssueRecords;
